/**
 * 작성자 : 홍선영
 * 날짜 : 2025.01.07
 * 기능 : 이달의 우수사원/금일 휴가자/생일자 목록 (사내용 상황판)
 */

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PhotoTableListStyle } from '../../assets/styles/PhotoTableList';
import IssueGuide from '../IssueGuide';
import GetFetchStatus from '../../hooks/GetFetchStatus';
import SmallTextLabel from '../button/SmallTextLabel';
import dayjs from 'dayjs';
import { todayYYYYMMDD } from '../../utils/formatDate';
import SkeletonTable from '../SkeletonTable';
import { useOutletContext } from 'react-router-dom';
import useFirstItemAtTop from '../../hooks/useFirstItemAtTop';

interface Iprops {
  autoRefresh: boolean;
}

interface IworkerLocation {
  cSeq: number;
  userName: string;
  cContents: string;
  partCdName: string;
  partName: string;
  uBdate: string;
  uBgubun: string;
  positionName: string;
  caName: string;
  fTime: string;
  sTime: string;
  caDepartment: string;
  fDate: string;
  uImg: string;
}

const HEIGHT_TITLE = 47;
const AVG_CHARACTERS_PER_LINE = 35; // 한 줄에 들어가는 평균 텍스트 길이 (띄어쓰기 포함)
const FLOW_SEC_PER_LINE = 3; // 1줄 당 플로우 초
const DEFAULT_LIST_HEIGHT = 174; // 우수사원/휴가자/생일자 리스트의 기본 높이값 (위젯높이를 결정할 동안 UI변동을 최소화 하기위함)
const ANIMATION_DELAY = 3; // 애니메이션 딜레이 시간 3 = 3초 후 실행

const stringTimeRange = (time1: string, time2: string) => {
  if (!time1 || !time2) return '';
  return `${time1.substring(0, 2)}:${time1.substring(2, 4)} ~ ${time2.substring(0, 2)}:${time2.substring(2, 4)}`;
};

const holidayRange = (holidayStr: string) => {
  const today = todayYYYYMMDD();
  const holidayDate = dayjs(holidayStr).format('YYYYMMDD');

  if (today === holidayDate) return '오늘';
  return `${dayjs(holidayDate, 'YYYYMMDD').format('YYYY년 MM월 DD일')} 까지`;
};

const getMMDD = (dateString: string) => {
  if (!dateString) return '';
  return `${dayjs(dateString, 'YYYYMMDD').format('MM월 DD일')}`;
};

export const CompanyUserList = ({ autoRefresh }: Iprops) => {
  const isSiteDashboard = autoRefresh; // 상황판 여부
  const { t } = useTranslation();
  const photoTableRef = useRef<HTMLDivElement>(null);
  const childRef = useRef<HTMLDivElement>(null);
  const childRef2 = useRef<HTMLDivElement>(null);
  const childRef3 = useRef<HTMLDivElement>(null);
  const [listHeight, setListHeight] = useState(DEFAULT_LIST_HEIGHT);
  const [slideDuration, setSlideDuration] = useState<null | number>(null);
  const [isScrollEnabled, setIsScrollEnabled] = useState(false);
  const [isScrollEnabled2, setIsScrollEnabled2] = useState(false);
  const [isScrollEnabled3, setIsScrollEnabled3] = useState(false);
  const size = useOutletContext<any>();

  const {
    data: excellentEmployeeList,
    isLoading: isLoadingExcellent,
    isError: isErrorExcellent,
    refetch: refetchExcellent,
  } = GetFetchStatus({ path: '/cexcellent/board', hCdOnly: { value: true, key: 'cCd' }, refetchInterval: 30 * 60 * 1000 });
  const {
    data: holidayEmployeeList,
    isLoading: isLoadingHoliay,
    isError: isErrorHoliday,
    refetch: refetchHoliday,
  } = GetFetchStatus({ path: '/attendance/board', hCdOnly: { value: true, key: 'cCd' }, refetchInterval: 30 * 60 * 1000 });
  const {
    data: birthdayEmployeeList,
    isLoading: isLoadingBirthday,
    isError: isErrorBirthday,
    refetch: refetchBirthday,
  } = GetFetchStatus({ path: '/user/board/birthday', hCdOnly: { value: true, key: 'hCd' }, refetchInterval: 30 * 60 * 1000 });

  const onClickRefresh = () => {
    refetchExcellent();
    refetchHoliday();
    refetchBirthday();
  };

  const holidayParentRef = useRef<HTMLDivElement>(null) as React.MutableRefObject<HTMLDivElement | null>;
  const holidayFirstItemRef = useRef<HTMLDivElement>(null) as React.MutableRefObject<HTMLDivElement | null>;
  const excellentParentRef = useRef<HTMLDivElement>(null) as React.MutableRefObject<HTMLDivElement | null>;
  const excellentFirstItemRef = useRef<HTMLDivElement>(null) as React.MutableRefObject<HTMLDivElement | null>;
  const birthdayParentRef = useRef<HTMLDivElement>(null) as React.MutableRefObject<HTMLDivElement | null>;
  const birthdayFirstItemRef = useRef<HTMLDivElement>(null) as React.MutableRefObject<HTMLDivElement | null>;

  const isExcellentListAtTop = useFirstItemAtTop(excellentParentRef, excellentFirstItemRef, excellentEmployeeList);
  const isHolidayListAtTop = useFirstItemAtTop(holidayParentRef, holidayFirstItemRef, holidayEmployeeList);
  const isBirthdayListAtTop = useFirstItemAtTop(birthdayParentRef, birthdayFirstItemRef, birthdayEmployeeList);

  // isAtTop이 true일 때 3초간 스크롤을 비활성화하고 이후 다시 활성화하는 로직
  useEffect(() => {
    if (isScrollEnabled && isExcellentListAtTop) {
      // isAtTop이 true가 되면 스크롤을 즉시 비활성화
      setIsScrollEnabled(false);
      const timer = setTimeout(() => {
        setIsScrollEnabled(true); // 3초 후에 스크롤을 다시 활성화
      }, 3000);
      return () => clearTimeout(timer);
    }
    return () => {}; // isAtTop이 false일 때 빈 cleanup 함수 반환
  }, [isExcellentListAtTop]);

  useEffect(() => {
    if (isScrollEnabled2 && isHolidayListAtTop) {
      setIsScrollEnabled2(false);
      const timer = setTimeout(() => {
        setIsScrollEnabled2(true);
      }, 3000);
      return () => clearTimeout(timer);
    }
    return () => {};
  }, [isHolidayListAtTop]);

  useEffect(() => {
    if (isScrollEnabled3 && isBirthdayListAtTop) {
      setIsScrollEnabled3(false);
      const timer = setTimeout(() => {
        setIsScrollEnabled3(true);
      }, 3000);
      return () => clearTimeout(timer);
    }
    return () => {};
  }, [isBirthdayListAtTop]);

  const loading = isLoadingExcellent || isLoadingHoliay || isLoadingBirthday || !excellentEmployeeList || !holidayEmployeeList || !birthdayEmployeeList;
  const loadingExcellent = isLoadingExcellent || !excellentEmployeeList || slideDuration === null;
  const loadingHoliday = isLoadingHoliay || !holidayEmployeeList;
  const loadingBirthday = isLoadingBirthday || !birthdayEmployeeList;
  const error = isErrorExcellent || isErrorHoliday || isErrorBirthday;

  // 텍스트 슬라이드 애니메이션 시간을 계산하는 함수
  const getTextFlowSeconds = (list: any[]) => {
    // 전체 텍스트 길이 계산
    const totalLength = list.reduce((sum, item) => sum + item.cContents.length, 0);
    // 아이템 수에 따라 추가적인 시간 계산 (리스트 길이에 비례)
    const adjustedListLengthFactor = list.length > 1 ? list.length * 1.2 : 1;
    // 총 애니메이션 시간을 반환
    const count = (totalLength / AVG_CHARACTERS_PER_LINE) * FLOW_SEC_PER_LINE * adjustedListLengthFactor;
    // 총 글자수가 적지만 목록은 2 이상일 때, 고정값 5적용 (너무 빠르게 흐르지 않도록)
    if (totalLength < 20 && list.length > 1) return 5;
    if (totalLength > 100 && totalLength <= 200) return count * 0.4;
    if (totalLength > 200 && totalLength <= 500) return count * 0.3;
    if (totalLength > 500 && totalLength <= 1000) return count * 0.1;
    if (totalLength > 1000) return count * 0.08;
    return count;
  };

  useEffect(() => {
    if (excellentEmployeeList) setSlideDuration(getTextFlowSeconds(excellentEmployeeList));
  }, [excellentEmployeeList]);

  useLayoutEffect(() => {
    if (photoTableRef.current && childRef.current) {
      // 데이터가 DOM에 반영된 후 높이를 측정하기 위해 setTimeout 사용
      setTimeout(() => {
        // 개별 목록의 높이 결정
        const eachListHeight = photoTableRef.current!.clientHeight / 3;
        setListHeight(eachListHeight);

        // 높이 비교해서 스크롤여부
        setIsScrollEnabled(eachListHeight - HEIGHT_TITLE < childRef.current!.scrollHeight);
      }, 0);
    }
  }, [photoTableRef, childRef, loading, slideDuration, size]); // data가 변경될 때마다 높이 비교

  useLayoutEffect(() => {
    if (photoTableRef.current && childRef2.current) {
      setTimeout(() => {
        const eachListHeight = photoTableRef.current!.clientHeight / 3;
        setIsScrollEnabled2(eachListHeight - HEIGHT_TITLE < childRef2.current!.scrollHeight);
      }, 0);
    }
  }, [photoTableRef, childRef2, loading, size]); // data가 변경될 때마다 높이 비교

  useLayoutEffect(() => {
    if (photoTableRef.current && childRef3.current) {
      setTimeout(() => {
        const eachListHeight = photoTableRef.current!.clientHeight / 3;
        setIsScrollEnabled3(eachListHeight - HEIGHT_TITLE < childRef3.current!.scrollHeight);
      }, 0);
    }
  }, [photoTableRef, childRef3, loading, size]); // data가 변경될 때마다 높이 비교

  if (error) return <IssueGuide />;

  return (
    <PhotoTableListStyle ref={photoTableRef}>
      <div className='one-third' style={{ height: listHeight }}>
        <div className='widget-header safety'>
          <div className='widget-title flex-between'>
            <span>
              {t('이 달의 우수사원')}
              <span className='badge'>{excellentEmployeeList?.length ?? 0}</span>
            </span>
            {!isSiteDashboard && (
              <div className='refresh-btn' onClick={onClickRefresh} role='presentation'>
                <span className='material-symbols-rounded'>refresh</span>
              </div>
            )}
          </div>
        </div>
        {loadingExcellent ? (
          <SkeletonTable rows={2} />
        ) : (
          <div className='widget-body photoTable' style={{ height: listHeight - HEIGHT_TITLE }} ref={excellentParentRef}>
            <div ref={childRef}>
              <div
                className='list'
                style={{
                  height: 'inherit',
                  animation: isScrollEnabled ? `slide ${slideDuration}s linear infinite` : '',
                  // paddingTop: isScrollEnabled ? '1rem' : '',
                }}
              >
                {excellentEmployeeList?.length > 0 ? (
                  excellentEmployeeList.map((el: IworkerLocation, i: number) => (
                    <ExcellentList
                      key={i}
                      img={el.uImg}
                      data1={`${el.userName} ${el.positionName}`}
                      data2={el.cContents}
                      data3={el.partCdName}
                      data4={undefined}
                      firstItemRef={i === 0 ? excellentFirstItemRef : null}
                    />
                  ))
                ) : (
                  <div className='noData'>No data.</div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
      <div className='one-third' style={{ height: listHeight }}>
        <div className='widget-header safety'>
          <div className='widget-title flex-between'>
            <span>
              {t('금일 휴가자')}
              <span className='badge'>{holidayEmployeeList?.length ?? 0}</span>
            </span>
          </div>
        </div>
        {loadingHoliday ? (
          <SkeletonTable rows={2} />
        ) : (
          <div className='widget-body photoTable' style={{ height: listHeight - HEIGHT_TITLE }} ref={holidayParentRef}>
            <div ref={childRef2}>
              <div
                style={{
                  height: 'inherit',
                  animation: isScrollEnabled2 ? `slide ${holidayEmployeeList.length * 3.5}s ${ANIMATION_DELAY}s linear infinite` : '',
                  animationFillMode: 'forwards',
                }}
              >
                {holidayEmployeeList?.length > 0 ? (
                  holidayEmployeeList.map((el: IworkerLocation, i: number) => (
                    <WorkerList
                      key={i}
                      img={el.uImg}
                      data1={`${el.caName} ${el.positionName}`}
                      data2={stringTimeRange(el.sTime, el.fTime)}
                      data3={el.caDepartment}
                      data4={holidayRange(el.fDate)}
                      firstItemRef={i === 0 ? holidayFirstItemRef : null}
                    />
                  ))
                ) : (
                  <div className='noData'>No data.</div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
      <div className='one-third' style={{ height: listHeight }}>
        <div className='widget-header safety'>
          <div className='widget-title flex-between'>
            <span>
              {t('금일 생일자')}
              <span className='badge'>{birthdayEmployeeList?.length ?? 0}</span>
            </span>
          </div>
        </div>
        {loadingBirthday ? (
          <SkeletonTable rows={2} />
        ) : (
          <div className='widget-body photoTable' style={{ height: listHeight - HEIGHT_TITLE }} ref={birthdayParentRef}>
            <div ref={childRef3}>
              <div
                style={{
                  height: 'inherit',
                  animation: isScrollEnabled3 ? `slide ${birthdayEmployeeList.length * 3.5}s ${ANIMATION_DELAY}s linear infinite` : '',
                }}
              >
                {birthdayEmployeeList?.length > 0 ? (
                  birthdayEmployeeList.map((el: IworkerLocation, i: number) => (
                    <WorkerList
                      key={i}
                      img={el.uImg}
                      data1={`${el.userName} ${el.positionName}`}
                      data2={el.uBgubun}
                      data3={el.partName}
                      data4={getMMDD(el.uBdate)}
                      firstItemRef={i === 0 ? birthdayFirstItemRef : null}
                    />
                  ))
                ) : (
                  <div className='noData'>No data.</div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </PhotoTableListStyle>
  );
};

const ExcellentList = ({ img, data1, data2, data3, data4, firstItemRef }: { img: string; data1: string; data2: string; data3: string; data4?: string; firstItemRef: any }) => {
  const randomString = Math.random().toString(36).substring(7); // 랜덤 문자열을 쿼리스트링으로 추가해서 이미지 캐싱 방지
  return (
    <div
      className='flex-col'
      style={{ gap: '0.5rem' }}
      ref={firstItemRef} // 첫 번째 아이템에만 ref 할당
    >
      <div className='flex-start'>
        <div className='imgWrapper'>{img ? <img src={`${img}?v=${randomString}`} alt='' /> : <span className='material-symbols-rounded'>person </span>}</div>
        <div className='flex-start'>
          <SmallTextLabel text={data1} color='violet' />
          <div className='text_disabled text-small'>{data3}</div>
        </div>
      </div>
      <div>
        <div className='text_secondary' style={{ whiteSpace: 'pre-line' }}>
          {data2}
        </div>
        {data4 && <div className='text_secondary'>{data4}</div>}
      </div>
    </div>
  );
};

const WorkerList = ({ img, data1, data2, data3, data4, firstItemRef }: { img: string; data1: string; data2: string; data3: string; data4?: string; firstItemRef: any }) => {
  const randomString = Math.random().toString(36).substring(7); // 랜덤 문자열을 쿼리스트링으로 추가해서 이미지 캐싱 방지
  return (
    <div
      className='flex-start'
      style={{ gap: '0.5rem', alignItems: 'start' }}
      ref={firstItemRef} // 첫 번째 아이템에만 ref 할당
    >
      <div className='imgWrapper'>{img ? <img src={`${img}?v=${randomString}`} alt='' /> : <span className='material-symbols-rounded'>person </span>}</div>
      <div className='col1' style={{ minWidth: '5rem' }}>
        <div className='flex-col'>
          <SmallTextLabel text={data1} color='violet' />
          <div className='text_disabled text-small'>{data3}</div>
        </div>
      </div>
      <div className='col2' style={{ overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: 2 }}>
        <div className='text_secondary'>{data2}</div>
        {data4 && <div className='text_secondary'>{data4}</div>}
      </div>
    </div>
  );
};
